import BusStop from '../models/bus_stop';
import MapMenu from '../lib/map_menu';
import UiInteraction from '../lib/ui_interaction';
import busPopup from '../templates/popups/bus';

export default class BusRouteLayer {
	constructor(route_id) {
		this.route_id = route_id;
		this.source_id = 'bus-route-source-' + this.route_id;
		this.layer_id = 'bus-route-layer-' + this.route_id;
		this.url = W.api_root + 'bus_routes/' + this.route_id;
		this.stop_markers = [];
		this.route_pattern_layers = [];
		this.visible = false;
		this.ignoreNextSetView = false;
		this.checkboxEl = document.getElementById('bus-route-layer-' + this.route_id);
	}

	add() {
		this.getData().then(() => {
			this.color = '#' + this.routeData.color;
			this.loadRoute(() => {
				this.visible = true;
				if (W.map.getZoom() > 15) {
					this.loadStops();
				}
				this.setBounds();
			});
			MapMenu.hide();
			W.loadVehiclePositions();
		});
	}

	remove() {
		this.visible = false;
		this.stop_markers.forEach((marker) => {
			marker.getElement().removeEventListener('click', this.openPopup);
			marker.remove();
		});
		this.route_pattern_layers.forEach((pattern_layer) => W.map.removeLayer(pattern_layer));
		W.vehicles.forEach((vehicle) => vehicle.marker.remove());
		let keepVehicles = [];

		// Remove this route's vehicle markers and remove them
		// from our tracking array W.vehicles
		W.vehicles.forEach((v) => {
			if (v.route_id === this.route_id) {
				v.marker.remove();
			} else {
				keepVehicles.push(v);
			}
		});
		W.vehicles = keepVehicles;
		// if no vehicle being tracked, clear the interval
		if (W.vehicles.length === 0) {
			clearInterval(W.vehiclePositionInterval);
			W.vehiclePositionInterval = undefined;
		}
	}

	async getData() {
		if (this.routeData) {
			return;
		}

		try {
			const response = await fetch(this.url);
			if (!response.ok) {
				throw new Error(`Response status: ${response.status}`);
			}

			const json = await response.json();
			this.routeData = json;
		} catch (error) {
			console.error(error.message);
		}
	}

	loadRoute(cb) {
		if (this.routeData.patterns.length > 0) {
			this.routeData.patterns.forEach((pattern, i) => {
				let pattern_source_id = this.source_id + '_' + i;
				if (!W.map.getSource(pattern_source_id)) {
					W.map.addSource(pattern_source_id, {
						type: 'geojson',
						data: pattern,
					});
				}

				W.map.addLayer({
					id: this.layer_id + '_' + i,
					type: 'line',
					source: pattern_source_id,
					paint: {
						'line-width': 3,
						'line-color': this.color,
					},
				});

				this.route_pattern_layers.push(this.layer_id + '_' + i);
			});
		}

		if (cb) {
			cb.call(this);
		}
	}

	loadStops() {
		// hide bus stop markers; they'll appear at zoom > 15
		document.querySelector('body').style.setProperty('--bus-stop-marker-opacity', 0);

		// add marker for each stop
		this.routeData.bus_stops.forEach((stop) => {
			const bus_stop = new BusStop(stop.stop_code);
			bus_stop.routes = stop.routes;

			const popup = new mapboxgl.Popup({ className: 'uw-popover uw-popover-bus-stops' })
				.setLngLat(stop.lnglat)
				.setHTML(bus_stop.popupContent())
				.setMaxWidth('350px')
				.on('open', (e) => {
					const popupDialogLink = e.target._content.querySelector('button[data-dialog-url]');
					popupDialogLink.addEventListener('click', UiInteraction.handleDialogLinkClick);

					if (typeof gtag !== 'undefined') {
						const gaValue = 'BUS_STOP: Stop #' + stop.human_code + ' - ' + stop.title;
						gtag('event', 'open-popup', { event_category: 'Popup', event_label: gaValue });
					}
				});

			const marker = new mapboxgl.Marker({ anchor: 'center', color: this.color })
				.setLngLat(stop.lnglat)
				.addClassName('bus-stop-marker')
				.addTo(W.map);

			marker.getElement().setAttribute('aria-label', 'Stop ' + stop.human_code + ' ' + stop.name);
			marker.getElement().setAttribute('tabindex', 0);
			marker.getElement().addEventListener('click', this.openPopup.bind(null, popup, bus_stop));

			this.stop_markers.push(marker);
		});
	}

	// add popup and then stop marker click event propagation
	openPopup(popup, bus_stop, e) {
		UiInteraction.clearAllPopups();
		popup.addTo(W.map);

		// Add accessibility support matching ui_interaction.js
		const el = popup.getElement();
		el.querySelector('.mapboxgl-popup-close-button').removeAttribute('aria-hidden');
		el.setAttribute('role', 'dialog');
		el.setAttribute('tabindex', '-1');
		el.setAttribute('aria-labelledby', 'popup-name');
		el.focus();

		// track open popup
		W.popups = [{ key: bus_stop.key }];

		popup.on('close', (e) => {
			const popupDialogLink = e.target._content.querySelector('button[data-dialog-url]');
			popupDialogLink.removeEventListener('click', UiInteraction.handleDialogLinkClick);
			UiInteraction.clearPopup(bus_stop.key);
		});

		e.stopPropagation();
	}

	// TODO: Add to layter utils?
	setBounds() {
		// Bail if we're meant to ignore the first setView
		if (this.ignoreNextSetView) {
			this.ignoreNextSetView = false;
			return false;
		}

		let bounds = this.routeData.patterns[0].coordinates.reduce((bounds, coordinate) => {
			return bounds.extend(coordinate);
		}, new mapboxgl.LngLatBounds());

		W.map.fitBounds(bounds, {
			maxZoom: 14,
		});
	}
}

/**
 * Load vehicle position markers for all visible bus route layers.
 * Assigned to W.loadVehiclePositions by the entry point.
 */
export function loadVehiclePositions() {
	// For each route, remove any vehicle markers and
	// re-add them if route layer is still visible
	['80', '81', '82', '84'].forEach(function (route_id) {
		const bus_route_layer = W.layers['bus_route_layer_' + route_id];
		let keepVehicles = [];
		// remove vehicle for this layer
		W.vehicles.forEach(function (v) {
			if (v.route_id === route_id) {
				v.marker.remove();
			} else {
				keepVehicles.push(v);
			}
		});
		W.vehicles = keepVehicles;

		// add vehicles if route layer is still visible
		if (bus_route_layer.visible) {
			const url = W.api_root + 'bus_routes/' + route_id + '/vehicle_positions';

			// get the route vehicle positions
			fetch(url)
				.then((response) => {
					if (!response.ok) {
						throw new Error(`HTTP error! Status: ${response.status}`);
					}
					return response.json();
				})
				.then((route) => {
					// Add vehicle positions if any
					// Our CSS marker's point defaults to 315 degrees
					// so we subtract 315 from the bus's bearing value
					if (route.vehicles_geojson) {
						// add each bus icon
						route.vehicles_geojson.features.forEach((vehicle) => {
							const marker_el = document.createElement('div');
							marker_el.className = 'live-bus';

							let innerHTML =
								'<span class="live-bus-route bearing-188">' +
								vehicle.properties.route_name +
								'</span><span class="live-bus-bearing" style="--bearing: ' +
								(parseInt(vehicle.properties.bearing) - 315) +
								'deg"></span>';
							innerHTML = innerHTML + '</span > ';

							marker_el.innerHTML = innerHTML;

							const popupData = {
								vehicle_id: vehicle.properties.vehicle_id,
								route: vehicle.properties.route_name,
								lastUpdated: vehicle.properties.lastUpdated,
								bearing: vehicle.properties.bearingCompass,
								occupancy_status: vehicle.properties.occupancy_status,
							};
							const popup = new mapboxgl.Popup({ className: 'uw-popover uw-popover-bus' })
								.setLngLat(vehicle.geometry.coordinates)
								.setHTML(busPopup(popupData))
								.setMaxWidth('350px');

							const vehicle_marker = new mapboxgl.Marker(marker_el, { anchor: 'bottom' })
								.setLngLat(vehicle.geometry.coordinates)
								.setPopup(popup)
								.addTo(W.map);

							vehicle_marker.getElement().addEventListener('click', (e) => {
								UiInteraction.clearAllPopups();
								popup.addTo(W.map);

								// Add accessibility support
								const popupEl = popup.getElement();
								popupEl.querySelector('.mapboxgl-popup-close-button').removeAttribute('aria-hidden');
								popupEl.setAttribute('role', 'dialog');
								popupEl.setAttribute('tabindex', '-1');
								popupEl.setAttribute('aria-labelledby', 'popup-name');
								popupEl.focus();

								// track open popup
								W.popups = [{ key: 'Bus_' + vehicle.properties.vehicle_id }];
								popup.on('close', () => {
									UiInteraction.clearPopup('Bus_' + vehicle.properties.vehicle_id);
								});
								e.stopPropagation();
							});

							W.vehicles.push({
								id: vehicle.properties.route_name,
								route_id: vehicle.properties.route_name,
								marker: vehicle_marker,
							});
						});

						if (W.vehiclePositionInterval === undefined) {
							W.vehiclePositionInterval = setInterval(function () {
								W.loadVehiclePositions();
							}, 30000);
						}
					}
				});
		}
	});
}
